/**
 * One form: what it asks, whom it targets, the statement under it, and —
 * for an administrator — who has signed and who has not.
 */

import { useId, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useTaxonomy } from '../admin/useTaxonomy';
import { useMembers } from '../admin/useMembers';
import { hasAtLeast, ROLE_DISPLAY_NAMES, isRole } from '../../types/roles';
import { isCallFailure } from '../../lib/callable';
import { FORM_DUE_DAYS_MAX, FORM_REASON_MAX } from '../../types/fieldLimits';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { BackLink } from '../../shared/BackLink';
import { CharCount } from '../../shared/CharCount';
import { formatDay } from '../../shared/dates';
import { useForm, useFormAssignments, useFormAction, type FormAction, type PublishResult } from './useForms';
import { ASSIGNMENT_LABEL, ASSIGNMENT_TONE, AUDIENCE_LABEL, FIELD_TYPE_LABEL, FORM_CATEGORY_LABEL, formActions, STATUS_LABEL, STATUS_TONE } from './types';
import { dueText, sortAssignments } from './MyFormsPage';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './Forms.module.css';

type Panel = 'publish' | 'retire' | null;

export function publishedMessage(r: PublishResult | undefined): string {
  if (r?.skipped) return 'Nothing changed since the last version, so no new version was published.';
  const n = r?.assignmentCount ?? 0;
  return `Published as version ${r?.versionNumber ?? ''}`.trim() + `. Assigned to ${n} ${n === 1 ? 'person' : 'people'}.`;
}

export function FormDetailPage() {
  const { formId = '' } = useParams();
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const roles = session?.roles ?? [];
  const admin = hasAtLeast(roles, 'admin');

  const record = useForm(orgId, formId);
  const assignments = useFormAssignments(orgId, formId, admin);
  const act = useFormAction(orgId, formId);
  const departments = useTaxonomy('department', Boolean(orgId));
  const members = useMembers(orgId);
  const departmentName = useMemo(() => {
    const byId = new Map((departments.data ?? []).map((d) => [d.id, d.name]));
    return (id: string) => byId.get(id) ?? id;
  }, [departments.data]);
  const memberName = useMemo(() => {
    const byId = new Map((members.data ?? []).map((m) => [m.uid, m.displayName || m.email]));
    return (id: string) => byId.get(id) ?? id;
  }, [members.data]);

  const [panel, setPanel] = useState<Panel>(null);
  const [dueDays, setDueDays] = useState('30');
  const [dueDate, setDueDate] = useState('');
  const [reason, setReason] = useState('');
  const [waiving, setWaiving] = useState<string | null>(null);
  const [waiveReason, setWaiveReason] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const ids = useId();
  const id = (n: string) => `${ids}-${n}`;

  function run(action: FormAction, message: (r: PublishResult) => string) {
    setDone(null);
    act.mutate(action, { onSuccess: (r) => { setDone(message(r ?? {})); setPanel(null); setWaiving(null); setReason(''); setWaiveReason(''); } });
  }

  if (record.isLoading) return <Spinner label="Opening the form" />;
  if (record.error || !record.data) {
    return (
      <div className={styles.body}>
        <PageState title={record.data === null ? 'That form is not here' : 'Could not open the form'} action={{ to: '/forms', label: 'Back to forms' }} tone="alert">
          {record.data === null ? 'It may have been removed, or the link may be wrong.' : isCallFailure(record.error) ? record.error.message : 'Something went wrong.'}
        </PageState>
      </div>
    );
  }

  const f = record.data;
  const can = formActions(f.status, roles);
  const busy = act.isPending;
  const rows = sortAssignments(assignments.data ?? []);
  const counts = rows.reduce((acc, a) => ({ ...acc, [a.status]: (acc[a.status] ?? 0) + 1 }), {} as Record<string, number>);
  const audience = f.targetAudience === 'all' ? 'Everyone'
    : f.targetAudience === 'departments' ? f.targetDepartmentIds.map(departmentName).join(', ')
    : f.targetAudience === 'roles' ? f.targetRoles.map((r) => (isRole(r) ? ROLE_DISPLAY_NAMES[r] : r)).join(', ')
    : f.targetUserIds.map(memberName).join(', ');

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerRow}>
          <div className={styles.titled}>
            <p className={styles.eyebrow}>Form{f.currentVersionNumber ? ` · Version ${f.currentVersionNumber}` : ''}</p>
            <div className={styles.titleLine}>
              <BackLink to="/forms" label="Back to forms" />
              <h1 className={styles.title}>{f.title}</h1>
            </div>
            <p className={own.status}>
              <span className={`${styles.tag} ${styles[STATUS_TONE[f.status]]}`}>{STATUS_LABEL[f.status]}</span>
              <span>{f.fields.length} {f.fields.length === 1 ? 'field' : 'fields'} · {AUDIENCE_LABEL[f.targetAudience]}</span>
            </p>
          </div>
          {(can.edit || can.publish || can.retire) && (
            <div className={styles.actions}>
              {can.edit && <Link to={`/forms/${f.id}/edit`} className={styles.btn}>Edit</Link>}
              {can.publish && <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} aria-expanded={panel === 'publish'} onClick={() => setPanel(panel === 'publish' ? null : 'publish')}>{f.status === 'published' ? 'Publish new version…' : 'Publish…'}</button>}
              {can.retire && <button type="button" className={styles.btn} disabled={busy} aria-expanded={panel === 'retire'} onClick={() => setPanel(panel === 'retire' ? null : 'retire')}>Retire…</button>}
            </div>
          )}
        </div>

        {panel === 'publish' && (
          <form className={own.panel} aria-labelledby={id('pub-t')} onSubmit={(e: FormEvent) => { e.preventDefault(); run({ name: 'publishForm', dueDaysDefault: dueDays, dueDateOverride: dueDate }, publishedMessage); }}>
            <h2 className={own.panelTitle} id={id('pub-t')}>{f.status === 'published' ? 'Publish a new version' : 'Publish this form'}</h2>
            <p className={styles.subtitle} style={{ marginTop: 0 }}>Everyone it targets is assigned it with a due date. Signed copies are kept against this exact version.</p>
            <div className={form.row}>
              <div className={form.field}>
                <label className={form.label} htmlFor={id('days')}>Due in <span className={form.optional}>(days)</span></label>
                <input id={id('days')} className={form.input} type="number" min={1} max={FORM_DUE_DAYS_MAX} step={1} value={dueDays} onChange={(e) => setDueDays(e.target.value)} disabled={Boolean(dueDate)} />
              </div>
              <div className={form.field}>
                <label className={form.label} htmlFor={id('date')}>Or due on <span className={form.optional}>(a date)</span></label>
                <input id={id('date')} className={form.input} type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </div>
            </div>
            <div className={styles.actions}>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>{busy ? 'Publishing…' : 'Publish and assign'}</button>
              <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
            </div>
          </form>
        )}
        {panel === 'retire' && (
          <form className={own.panel} aria-labelledby={id('ret-t')} onSubmit={(e: FormEvent) => { e.preventDefault(); run({ name: 'retireForm', reason }, () => 'Retired. Signed copies stay on record; no new assignments are made.'); }}>
            <h2 className={own.panelTitle} id={id('ret-t')}>Retire this form?</h2>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('reason')}>Reason <span className={form.optional}>(optional)</span></label>
              <textarea id={id('reason')} className={form.textarea} rows={3} maxLength={FORM_REASON_MAX} value={reason} onChange={(e) => setReason(e.target.value.slice(0, FORM_REASON_MAX))} />
              <CharCount value={reason} max={FORM_REASON_MAX} />
            </div>
            <div className={styles.actions}>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>{busy ? 'Retiring…' : 'Retire'}</button>
              <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
            </div>
          </form>
        )}
      </header>

      <div className={styles.body}>
        {done && <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">{done}</div>}
        {act.error && <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">{isCallFailure(act.error) ? act.error.message : 'That could not be done.'}</div>}

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Details</h2>
          <dl className={styles.facts}>
            <div><dt className={styles.factTerm}>Record type</dt><dd className={styles.factValue}>{FORM_CATEGORY_LABEL[f.formCategory]}</dd></div>
            <div><dt className={styles.factTerm}>Signed again</dt><dd className={`${styles.factValue} ${f.renewalIntervalMonths ? '' : styles.absent}`}>{f.renewalIntervalMonths ? `every ${f.renewalIntervalMonths} months` : 'once'}</dd></div>
            <div><dt className={styles.factTerm}>Assigned to</dt><dd className={styles.factValue}>{audience || '—'}</dd></div>
            <div><dt className={styles.factTerm}>Published</dt><dd className={`${styles.factValue} ${f.publishedAt ? '' : styles.absent}`}>{formatDay(f.publishedAt)}</dd></div>
            <div><dt className={styles.factTerm}>Last updated</dt><dd className={`${styles.factValue} ${f.updatedAt ? '' : styles.absent}`}>{formatDay(f.updatedAt)}</dd></div>
          </dl>
        </section>

        {f.description && <section className={styles.section}><h2 className={styles.sectionTitle}>Description</h2><p className={own.prose}>{f.description}</p></section>}
        {f.instructions && <section className={styles.section}><h2 className={styles.sectionTitle}>Instructions</h2><p className={own.prose}>{f.instructions}</p></section>}

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Fields</h2>
          {f.fields.length === 0 ? <p className={`${own.prose} ${own.empty}`}>No fields yet.</p> : (
            <ol className={own.preview}>
              {f.fields.map((fd) => (
                <li key={fd.id} className={own.previewItem}>
                  <p className={own.previewLabel}>{fd.label}{fd.required && ' *'}</p>
                  <span className={own.previewMeta}>{FIELD_TYPE_LABEL[fd.type]}{fd.options.length ? ` · ${fd.options.join(' / ')}` : ''}{fd.description ? ` — ${fd.description}` : ''}</span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Attestation</h2>
          {f.requiresLegalAttestation ? <p className={own.statement}>{f.legalAttestationText}</p> : <p className={`${own.prose} ${own.empty}`}>No legal attestation is required on this form.</p>}
        </section>

        {admin && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Assignments{rows.length ? ` · ${rows.length}` : ''}</h2>
            {assignments.isLoading ? <Spinner label="Loading assignments" /> : rows.length === 0 ? (
              <p className={`${own.prose} ${own.empty}`}>{f.status === 'draft' ? 'Nobody is assigned until it is published.' : 'Nobody has been assigned this yet.'}</p>
            ) : (
              <>
                <p className={own.count} role="status">
                  {(['overdue', 'pending', 'completed', 'waived'] as const).filter((s) => counts[s]).map((s) => `${counts[s]} ${ASSIGNMENT_LABEL[s].toLowerCase()}`).join(' · ')}
                </p>
                <div className={own.tableWrap}>
                  <table className={own.table}>
                    <thead><tr><th scope="col">Person</th><th scope="col">Status</th><th scope="col">Due</th><th scope="col">Signed</th><th scope="col"><span className="vh">Actions</span></th></tr></thead>
                    <tbody>
                      {rows.map((a) => {
                        const due = dueText(a);
                        const open = a.status === 'pending' || a.status === 'overdue';
                        return (
                          <RowGroup key={a.id}>
                            <tr>
                              <td>{a.userDisplayName ?? a.userEmail ?? a.userId}{a.userEmail && a.userDisplayName && <span className={own.sub}>{a.userEmail}</span>}</td>
                              <td><span className={`${styles.tag} ${styles[ASSIGNMENT_TONE[a.status]]}`}>{ASSIGNMENT_LABEL[a.status]}</span>{a.status === 'waived' && a.waivedReason && <span className={own.sub}>{a.waivedReason}</span>}</td>
                              <td className={`${own.num} ${due.cls ? own[due.cls] : ''}`}>{due.text}</td>
                              <td className={`${own.num} ${a.completedAt ? '' : own.muted}`}>{formatDay(a.completedAt)}</td>
                              <td>
                                {a.submissionId && <Link to={`/forms/submissions/${a.submissionId}`} className={styles.btn}>View receipt</Link>}
                                {open && <button type="button" className={styles.btn} disabled={busy} aria-expanded={waiving === a.id} onClick={() => { setWaiving(waiving === a.id ? null : a.id); setWaiveReason(''); }}>Waive…</button>}
                              </td>
                            </tr>
                            {waiving === a.id && (
                              <tr><td colSpan={5}>
                                <form className={own.panel} style={{ marginTop: 0 }} aria-labelledby={id(`w-${a.id}`)} onSubmit={(e) => { e.preventDefault(); if (waiveReason.trim()) run({ name: 'waiveAssignment', assignmentId: a.id, reason: waiveReason }, () => `Waived for ${a.userDisplayName ?? a.userEmail ?? 'that person'}.`); }}>
                                  <h3 className={own.panelTitle} id={id(`w-${a.id}`)}>Waive for {a.userDisplayName ?? a.userEmail}</h3>
                                  <div className={form.field}>
                                    <label className={form.label} htmlFor={id(`wr-${a.id}`)}>Reason</label>
                                    <textarea id={id(`wr-${a.id}`)} className={form.textarea} rows={2} required maxLength={FORM_REASON_MAX} value={waiveReason} onChange={(e) => setWaiveReason(e.target.value.slice(0, FORM_REASON_MAX))} />
                                    <CharCount value={waiveReason} max={FORM_REASON_MAX} />
                                  </div>
                                  <div className={styles.actions}>
                                    <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy || !waiveReason.trim()}>Waive</button>
                                    <button type="button" className={styles.btn} onClick={() => setWaiving(null)}>Cancel</button>
                                  </div>
                                </form>
                              </td></tr>
                            )}
                          </RowGroup>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        )}
      </div>
    </>
  );
}

/** Two table rows that belong together; a fragment with a key. */
function RowGroup({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
